// Government-grade institutional content sections for the GIISDEC public portal.
// These sections present validation-safe, non-speculative content only. No demo,
// estimated, or sample figures are displayed. Where data is pending departmental
// approval, a clear "Content Under Validation" status is shown instead.
//
// This file deliberately contains NO numerical statistics, project names,
// mineral data, or investment opportunities — only approved institutional copy
// and validation-safe placeholders.
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ShieldCheck,
  Map as MapIcon,
  Database,
  Gem,
  Briefcase,
  FileCheck2,
  Layers,
  Building2,
  Info,
  Clock,
  BarChart3,
  FileText,
  FolderOpen,
  UserPlus,
  LifeBuoy,
} from 'lucide-react';

// ─── Shared validation-safe status indicator ────────────────────────────────
export const ValidationStatus: React.FC<{ label?: string; className?: string }> = ({
  label = 'Content Under Validation',
  className = '',
}) => (
  <span
    className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-eds-pill border bg-eds-gold/15 text-eds-earth border-eds-gold/30 ${className}`}
  >
    <Clock className="w-3.5 h-3.5" /> {label}
  </span>
);

// ─── About the Platform ─────────────────────────────────────────────────────
export const AboutPlatformSection: React.FC = () => (
  <section className="py-16 bg-eds-surface" aria-labelledby="about-platform-heading">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <div className="inline-flex items-center gap-2 text-eds-emerald-2 text-sm font-medium uppercase tracking-wider mb-3">
          <Building2 className="w-4 h-4" /> About the Platform
        </div>
        <h2 id="about-platform-heading" className="text-3xl font-bold text-eds-fg mb-4">
          About the Platform
        </h2>
        <p className="text-eds-muted-fg mb-4">
          The GIISDEC Geological Information &amp; Investment Platform (GIISDEC GIIP) serves as a
          centralized platform for geological information management, GIS-enabled
          operations, investor engagement, project visibility, and digital service
          delivery.
        </p>
        <p className="text-eds-muted-fg">
          The platform supports secure access to approved geological datasets, mineral
          resource information, spatial intelligence, project information, and investor
          services through a unified digital environment.
        </p>
        <Link
          to="/about"
          className="inline-flex items-center gap-2 text-eds-emerald-2 font-medium hover:gap-3 transition-all mt-6"
        >
          Learn more <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="bg-gradient-to-br from-eds-surface-2 to-eds-success/12 rounded-eds-lg p-8 border border-eds-border">
        <div className="space-y-4">
          {[
            { icon: Database, title: 'Approved Datasets', desc: 'Secure access to validated geological information resources.' },
            { icon: MapIcon, title: 'GIS-Enabled Operations', desc: 'Spatial intelligence supporting planning and decision-making.' },
            { icon: Briefcase, title: 'Investor Engagement', desc: 'Structured onboarding and regulatory guidance services.' },
          ].map((s) => (
            <div key={s.title} className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-eds-sm bg-eds-emerald-2 flex items-center justify-center flex-shrink-0">
                <s.icon className="w-4 h-4 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-eds-fg">{s.title}</h4>
                <p className="text-sm text-eds-muted-fg">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ─── Core Service Areas ─────────────────────────────────────────────────────
const CORE_SERVICES = [
  {
    icon: Database,
    title: 'Geological Information Services',
    desc: 'Access approved geological datasets, mineral occurrence information, survey records, and related resource intelligence.',
  },
  {
    icon: MapIcon,
    title: 'GIS and Spatial Intelligence',
    desc: 'Explore interactive maps, geospatial layers, and location-based information supporting planning, analysis, and decision-making.',
  },
  {
    icon: Briefcase,
    title: 'Investor Services',
    desc: 'Access investor onboarding services, project information, regulatory guidance, and approved investment opportunities.',
  },
  {
    icon: FileCheck2,
    title: 'Licensing and Regulatory Support',
    desc: 'Access information relating to permits, licensing requirements, approvals, and regulatory procedures.',
  },
];

export const CoreServiceAreasSection: React.FC = () => (
  <section className="py-16 bg-eds-bg" aria-labelledby="core-services-heading">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10">
        <h2 id="core-services-heading" className="text-3xl font-bold text-eds-fg">
          Core Service Areas
        </h2>
        <p className="text-eds-muted-fg mt-2 max-w-2xl mx-auto">
          Institutional service domains delivered through the GIISDEC digital environment.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {CORE_SERVICES.map((s) => (
          <div
            key={s.title}
            className="bg-eds-surface border border-eds-border rounded-eds-md p-6 hover:shadow-eds-md transition flex items-start gap-4"
          >
            <div className="w-11 h-11 rounded-eds-md bg-eds-emerald-2/10 flex items-center justify-center flex-shrink-0">
              <s.icon className="w-5 h-5 text-eds-emerald-2" />
            </div>
            <div>
              <h3 className="font-semibold text-eds-fg">{s.title}</h3>
              <p className="text-sm text-eds-muted-fg mt-1.5">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Strategic Projects (validation-safe) ───────────────────────────────────
export const StrategicProjectsSection: React.FC = () => (
  <section className="py-16 bg-eds-surface" aria-labelledby="strategic-projects-heading">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-8">
        <h2 id="strategic-projects-heading" className="text-3xl font-bold text-eds-fg">
          Strategic Projects
        </h2>
        <div className="mt-4 flex justify-center">
          <ValidationStatus />
        </div>
      </div>
      <div className="bg-eds-bg border border-eds-border rounded-eds-lg p-8 text-center">
        <div className="w-12 h-12 rounded-eds-md bg-eds-surface-2 flex items-center justify-center mx-auto mb-4">
          <Layers className="w-6 h-6 text-eds-muted-fg" />
        </div>
        <p className="text-eds-muted-fg max-w-2xl mx-auto">
          Project information relating to geological development, mineral resource
          initiatives, and sector development programmes will be published following
          departmental validation and approval.
        </p>
      </div>
    </div>
  </section>
);

// ─── Mineral Index (validation-safe schema preview) ─────────────────────────
const MINERAL_INDEX_FIELDS: { field: string; status: string }[] = [
  { field: 'Mineral Name', status: 'Pending GIISDEC Validation' },
  { field: 'Mineral Category', status: 'Pending GIISDEC Validation' },
  { field: 'Region', status: 'Pending GIISDEC Validation' },
  { field: 'Geological Context', status: 'Pending GIISDEC Validation' },
  { field: 'Investment Relevance', status: 'Pending GIISDEC Validation' },
  { field: 'GIS Reference', status: 'Pending GIISDEC Validation' },
  { field: 'Data Status', status: 'Pending GIISDEC Validation' },
  { field: 'Access Level', status: 'Pending GIISDEC Validation' },
];

export const MineralIndexSection: React.FC = () => (
  <section className="py-16 bg-eds-bg" aria-labelledby="mineral-index-heading">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center gap-2 mb-3">
        <Gem className="w-6 h-6 text-eds-earth" />
        <h2 id="mineral-index-heading" className="text-3xl font-bold text-eds-fg">
          Mineral Index
        </h2>
      </div>
      <p className="text-eds-muted-fg max-w-3xl mb-4">
        The Mineral Index serves as a structured catalogue of approved mineral
        information, geological references, regional mineral occurrences, and
        investment-relevant resource intelligence.
      </p>

      <div className="mb-8">
        <ValidationStatus />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {MINERAL_INDEX_FIELDS.map((f) => (
          <div key={f.field} className="bg-eds-surface border border-eds-border rounded-eds-md p-5">
            <div className="text-xs text-eds-muted-fg uppercase tracking-wider">{f.field}</div>
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-eds-earth">
              <Clock className="w-3.5 h-3.5" /> {f.status}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Investment Opportunities (validation-safe) ─────────────────────────────
export const InvestmentOpportunitiesSection: React.FC = () => (
  <section className="py-16 bg-eds-surface" aria-labelledby="investment-opportunities-heading">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-8">
        <h2 id="investment-opportunities-heading" className="text-3xl font-bold text-eds-fg">
          Investment Opportunities
        </h2>
        <div className="mt-4 flex justify-center">
          <ValidationStatus />
        </div>
      </div>
      <div className="bg-eds-success/12 border border-eds-success/30 rounded-eds-lg p-8 text-center">
        <div className="w-12 h-12 rounded-eds-md bg-eds-emerald-2/10 flex items-center justify-center mx-auto mb-4">
          <Briefcase className="w-6 h-6 text-eds-emerald-2" />
        </div>
        <p className="text-eds-fg max-w-2xl mx-auto">
          Approved investment opportunities and strategic projects will be published
          following departmental validation and approval.
        </p>
        <Link
          to="/investor-login"
          className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-eds-emerald-2 hover:bg-eds-primary text-eds-primary-fg rounded-eds-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-bg"
        >
          Investor Access <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </section>
);

// ─── GIS Portal ─────────────────────────────────────────────────────────────
export const GisPortalSection: React.FC = () => (
  <section className="py-16 bg-eds-bg" aria-labelledby="gis-portal-heading">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
      <div>
        <div className="inline-flex items-center gap-2 text-eds-earth text-sm font-medium uppercase tracking-wider mb-3">
          <MapIcon className="w-4 h-4" /> Public Geological GIS
        </div>
        <h2 id="gis-portal-heading" className="text-3xl font-bold text-eds-fg mb-4">
          Public Geological GIS
        </h2>
        <p className="text-eds-muted-fg mb-6">
          Access approved geospatial information, geological layers, spatial datasets,
          and location-based resources through the Public Geological GIS. GIS content and
          access are governed by approved departmental datasets and access controls.
        </p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-eds-fg mb-6">
          {[
            'Interactive Mapping',
            'Spatial Search',
            'Layer Visualization',
            'Geological Information Access',
            'Dataset Discovery',
            'GIS Data Requests',
          ].map((feature) => (
            <li key={feature} className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-eds-earth flex-shrink-0" /> {feature}
            </li>
          ))}
        </ul>
        <Link
          to="/gis"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-eds-gold hover:bg-eds-gold text-eds-primary-fg rounded-eds-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-bg"
        >
          <MapIcon className="w-4 h-4" /> Open Public GIS
        </Link>
      </div>
      <div className="rounded-eds-lg border border-eds-border bg-eds-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-eds-fg">Spatial Resources</h3>
          <ValidationStatus label="Content Under Validation" />
        </div>
        <p className="text-sm text-eds-muted-fg">
          Published GIS layers, datasets, and interactive resources will be made
          available following departmental validation and approval. Access to spatial
          resources is governed by role-based controls.
        </p>
        <div className="mt-5 flex items-start gap-2 text-xs text-eds-muted-fg">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Publicly disclosed layers are open to everyone. Additional spatial resources require authorized credentials issued by GIISDEC.</span>
        </div>
      </div>
    </div>
  </section>
);

// ─── Platform Services (institutional, validation-safe) ─────────────────────
const PLATFORM_SERVICES = [
  { icon: Database, title: 'Geological Information Services', desc: 'Access approved geological information and resource datasets.' },
  { icon: MapIcon, title: 'Public Geological GIS', desc: 'Explore maps, layers, and spatial information resources.' },
  { icon: Briefcase, title: 'Investor Services', desc: 'Access investor onboarding and information services.' },
  { icon: FolderOpen, title: 'Project Information', desc: 'View approved project information and development initiatives.' },
  { icon: FileCheck2, title: 'Regulatory Support', desc: 'Access licensing and regulatory guidance.' },
  { icon: BarChart3, title: 'Executive Insights', desc: 'Support operational oversight through approved reporting and analytics.' },
];

export const PlatformServicesSection: React.FC = () => (
  <section className="py-16 bg-eds-bg" aria-labelledby="platform-services-heading">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10">
        <h2 id="platform-services-heading" className="text-3xl font-bold text-eds-fg">
          Platform Services
        </h2>
        <p className="text-eds-muted-fg mt-2 max-w-3xl mx-auto">
          The GIISDEC platform provides secure access to approved information, services,
          and digital workflows supporting geological resource management and investment
          facilitation.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PLATFORM_SERVICES.map((s) => (
          <div key={s.title} className="bg-eds-surface border border-eds-border rounded-eds-md p-6 hover:shadow-eds-md transition">
            <div className="w-11 h-11 rounded-eds-md bg-eds-emerald-2/10 flex items-center justify-center mb-3">
              <s.icon className="w-5 h-5 text-eds-emerald-2" />
            </div>
            <h3 className="font-semibold text-eds-fg">{s.title}</h3>
            <p className="text-sm text-eds-muted-fg mt-2">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Information and Services (institutional, validation-safe) ───────────────
const INFORMATION_SERVICES = [
  { icon: Database, title: 'Geological Information', desc: 'Approved geological datasets and survey information.' },
  { icon: MapIcon, title: 'GIS Services', desc: 'Interactive maps and spatial information resources.' },
  { icon: Briefcase, title: 'Investor Services', desc: 'Investor onboarding and information support.' },
  { icon: FileCheck2, title: 'Licensing Support', desc: 'Regulatory guidance and licensing information.' },
  { icon: FolderOpen, title: 'Project Information', desc: 'Strategic projects and development initiatives.' },
  { icon: FileText, title: 'Information Requests', desc: 'Access requests for approved datasets and resources.' },
];

export const InformationServicesSection: React.FC = () => (
  <section className="py-16 bg-eds-surface" aria-labelledby="information-services-heading">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10">
        <h2 id="information-services-heading" className="text-3xl font-bold text-eds-fg">
          Information and Services
        </h2>
        <p className="text-eds-muted-fg mt-2 max-w-3xl mx-auto">
          Supporting geological information management, investor engagement, GIS
          operations, regulatory services, and resource development activities.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {INFORMATION_SERVICES.map((s) => (
          <div key={s.title} className="bg-eds-bg border border-eds-border rounded-eds-md p-6 hover:shadow-eds-md transition flex items-start gap-4">
            <div className="w-11 h-11 rounded-eds-md bg-eds-surface-2 flex items-center justify-center flex-shrink-0">
              <s.icon className="w-5 h-5 text-eds-fg" />
            </div>
            <div>
              <h3 className="font-semibold text-eds-fg">{s.title}</h3>
              <p className="text-sm text-eds-muted-fg mt-1.5">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Investor Portal ────────────────────────────────────────────────────────
export const InvestorPortalSection: React.FC = () => (
  <section className="py-16 bg-eds-surface" aria-labelledby="investor-portal-heading">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
      <div className="rounded-eds-lg border border-eds-border bg-gradient-to-br from-eds-surface to-eds-success/12 p-6 order-2 lg:order-1">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { icon: UserPlus, label: 'Investor Registration' },
            { icon: FileText, label: 'Information Requests' },
            { icon: FolderOpen, label: 'Project Access' },
            { icon: FileCheck2, label: 'Document Management' },
            { icon: ShieldCheck, label: 'Regulatory Guidance' },
            { icon: LifeBuoy, label: 'Investor Support' },
          ].map((f) => (
            <div key={f.label} className="flex items-center gap-2 bg-eds-surface border border-eds-border rounded-eds-md px-3 py-2.5">
              <f.icon className="w-4 h-4 text-eds-emerald-2 flex-shrink-0" />
              <span className="text-sm font-medium text-eds-fg">{f.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="order-1 lg:order-2">
        <div className="inline-flex items-center gap-2 text-eds-emerald-2 text-sm font-medium uppercase tracking-wider mb-3">
          <Briefcase className="w-4 h-4" /> Investor Portal
        </div>
        <h2 id="investor-portal-heading" className="text-3xl font-bold text-eds-fg mb-4">
          Investor Portal
        </h2>
        <p className="text-eds-muted-fg mb-6">
          A secure environment supporting investor registration, information access,
          project engagement, document management, and service requests.
        </p>
        <Link
          to="/investor-login"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-eds-emerald-2 hover:bg-eds-primary text-eds-primary-fg rounded-eds-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-bg"
        >
          Investor Access <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </section>
);
